import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';

interface ProfitabilityData {
    readonly date: string;
    readonly revenueRon: number;
    readonly interestIncomeRon: number;
    readonly lostContractsRon: number;
    readonly netProfitRon: number;
}

interface ProfitabilityChartProps {
    readonly data: readonly ProfitabilityData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

const profitAxisFormatter: Highcharts.AxisLabelsFormatterCallbackFunction = function () {
    return Highcharts.numberFormat(Number(this.value), 0, ',', ' ');
};

function ProfitabilityChart({
    data,
    loading = false,
    currency = 'EUR',
}: ProfitabilityChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currSuffix = currency === 'EUR' ? 'EUR' : 'RON';
    const categories = data.map(d => d.date);

    const options: Highcharts.Options = {
        chart: {
            type: 'area',
            height: 350,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories,
            tickmarkPlacement: 'on',
            title: {
                text: t('statistics.bi.period', 'Period'),
            },
            labels: {
                rotation: -45,
                style: {
                    fontSize: '10px',
                },
            },
        },
        yAxis: {
            title: {
                text: `${t('statistics.bi.amount', 'Amount')} (${currSuffix})`,
            },
            labels: {
                formatter: profitAxisFormatter,
            },
        },
        tooltip: {
            shared: true,
            valueDecimals: 0,
            valueSuffix: ` ${currSuffix}`,
        },
        plotOptions: {
            area: {
                stacking: undefined,
                lineColor: '#666666',
                lineWidth: 1,
                marker: {
                    lineWidth: 1,
                    lineColor: '#666666',
                },
            },
        },
        series: [
            {
                type: 'area',
                name: t('statistics.bi.revenue', 'Revenue'),
                data: data.map(d => Number(d.revenueRon) || 0),
                color: '#1890ff',
                fillOpacity: 0.3,
            },
            {
                type: 'area',
                name: t('statistics.bi.interestIncome', 'Interest Income'),
                data: data.map(d => Number(d.interestIncomeRon) || 0),
                color: '#52c41a',
                fillOpacity: 0.3,
            },
            {
                type: 'spline',
                name: t('statistics.bi.netProfit', 'Net Profit'),
                data: data.map(d => Number(d.netProfitRon) || 0),
                color: '#fa541c',
                lineWidth: 3,
                marker: {
                    enabled: true,
                    radius: 4,
                },
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default ProfitabilityChart;
